/**
 * Groov Backend Integration
 *
 * Handles all communication between the Shopify theme and the
 * Groov backend at api.groov-enterprise.com.
 *
 * Auth is token-based. On login the backend returns a session token,
 * which is stored in localStorage and sent as an Authorization header.
 */

// Which backend this theme talks to. Set per store in the theme editor
// (Theme settings → Groov backend → "Backend origin"); theme.liquid publishes it
// as window.GROOV_API_ORIGIN before this file loads.
//
// WHY A SETTING AND NOT AN EDIT HERE: this line used to be hand-swapped between
// production / staging / localhost, with the alternatives parked in comments. That
// makes the target a property of whoever last edited the file rather than of the
// store, and one careless merge points the LIVE storefront at a test backend.
//
// The default is PRODUCTION on purpose, and the asymmetry is deliberate: an
// unconfigured test store reading production is a read-only annoyance, while an
// unconfigured production store calling stagingv3 would put real buyers on a test
// database. The environment that must be configured is therefore the safe one.
const GROOV_API_DEFAULT_ORIGIN = 'https://api.groov-enterprise.com';

// Origin only — no trailing slash, no /api/v1 (appended here so the setting can't
// drift from the version prefix the theme actually calls).
const GROOV_API =
  String(window.GROOV_API_ORIGIN || GROOV_API_DEFAULT_ORIGIN).replace(/\/+$/, '') + '/api/v1';


const GROOV_TOKEN_KEY = 'groov_session_token';
const GROOV_APPLE_ERROR_KEY = 'groov_apple_error';

// The Apple sign-in return leg, as a promise anything reading the session can wait
// on. Assigned at the bottom of this file, where the redemption is kicked off.
//
// It exists because redeeming the one-time code is a network round trip that starts
// while this (deferred) script runs and is still in flight when DOMContentLoaded
// fires. A page that gates on checkSession() would otherwise ask the backend before
// the token was stored, be told `authenticated: false`, and bounce a customer who
// just signed in successfully to the login page — and because the code is single
// use and the URL is already scrubbed, they could not simply reload. It is a race,
// so it survives a manual test and fails for a real buyer.
//
// Null until this file finishes executing (nothing can call in before that, since
// this file defines GroovBackend), and resolved-immediately on every page load with
// no Apple parameters — so it costs nothing anywhere else.
let groovAppleReturn = null;

function groovHeaders(extra) {
  const headers = { 'Accept': 'application/json' };
  const token = localStorage.getItem(GROOV_TOKEN_KEY);
  if (token) {
    headers['Authorization'] = 'Bearer ' + token;
  }
  return Object.assign(headers, extra);
}

async function parseResponse(res) {
  try {
    const data = await res.json();
    if (!res.ok && !data.error) {
      data.error = data.message || 'Request failed';
    }
    return data;
  } catch (e) {
    return { error: 'Unexpected server response' };
  }
}

const GroovBackend = {
  // ── Orders ────────────────────────────────────────────────

  /**
   * An order's scan state, looked up by its own _groov_order_token.
   * Unauthenticated on purpose — the token IS the credential, so the customer
   * order page can ask without a Groov session.
   *
   * Returns { order_slug, shopify_order_name, scan_link, pairs_needing_details }
   * or { error }. `pairs_needing_details` is the authoritative "does this order
   * still need scans?" — the same count the account page's Scan Status column
   * uses. Callers must FAIL OPEN on error: hiding the scan link strands a buyer
   * with no way back in, while showing it needlessly costs nothing.
   */
  async orderByToken(token) {
    try {
      const res = await fetch(`${GROOV_API}/consumer_orders/by_token?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: groovHeaders()
      });
      return await parseResponse(res);
    } catch (e) {
      console.error('Order lookup failed:', e);
      return { error: 'Order lookup failed' };
    }
  },

  // ── Auth ──────────────────────────────────────────────────

  /** Quick client-side check — true if a token exists in localStorage. */
  hasToken() {
    return !!localStorage.getItem(GROOV_TOKEN_KEY);
  },

  /** Check if user has an active session. Returns { authenticated, user } */
  async checkSession() {
    // Never answer "are they signed in?" while an Apple sign-in is still being
    // redeemed — see groovAppleReturn.
    if (groovAppleReturn) {
      try { await groovAppleReturn; } catch (e) { /* a failed redemption just means no session */ }
    }

    try {
      const res = await fetch(`${GROOV_API}/session`, {
        method: 'GET',
        headers: groovHeaders()
      });
      const data = await parseResponse(res);
      if (data.token) {
        localStorage.setItem(GROOV_TOKEN_KEY, data.token);
      }
      return data;
    } catch (e) {
      console.error('Session check failed:', e);
      return { authenticated: false };
    }
  },

  /** Log in with email + password. Returns { user, token } on success or { error } on failure. */
  async login(email, password) {
    const res = await fetch(`${GROOV_API}/session`, {
      method: 'POST',
      headers: groovHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ email_address: email, password: password })
    });
    const data = await parseResponse(res);
    if (data.token) {
      localStorage.setItem(GROOV_TOKEN_KEY, data.token);
    }
    return data;
  },

  /** Sign in / sign up with Google. Posts the Google Identity Services ID token
   *  to the backend, which verifies it, finds-or-creates the account, and returns
   *  { user, token }. Stores the session token exactly like login() — no Shopify
   *  customer account or derived password (that was the old theme's bridge). */
  async googleLogin(idToken) {
    const res = await fetch(`${GROOV_API}/session/google`, {
      method: 'POST',
      headers: groovHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ id_token: idToken })
    });
    const data = await parseResponse(res);
    if (data.token) {
      localStorage.setItem(GROOV_TOKEN_KEY, data.token);
    }
    return data;
  },

  /** Where the "Continue with Apple" button sends the browser.
   *
   *  Unlike Google, this is a full-page redirect rather than a token posted from
   *  JS. Apple's JS SDK only runs on domains verified by serving a file at the
   *  domain root, which Shopify cannot do and which we could never do for a
   *  myshopify.com domain we don't own. So the backend brokers the whole trip and
   *  the storefront is never registered with Apple at all.
   *
   *  `returnTo` must be an absolute URL on an origin the backend allowlists
   *  (APPLE_RETURN_ORIGINS), or it refuses to send the customer back. */
  appleSignInUrl(returnTo) {
    return `${GROOV_API}/session/apple/start?return_to=${encodeURIComponent(returnTo)}`;
  },

  /** The return leg of Apple sign-in. Runs on every page load (see the call at the
   *  bottom of this file) because the customer lands back on whichever page the
   *  button was on, which is not necessarily one that renders the button.
   *
   *  The redirect carries a single-use code, never the session token itself — a
   *  token in a URL ends up in history, the Referer of the next request, and any
   *  analytics on the landing page. This trades it for the real token over POST
   *  and then scrubs the URL so a refresh doesn't retry a spent code.
   *
   *  Returns the session data on success, null when there was nothing to do. */
  async consumeAppleReturn() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('groov_apple');
    const error = params.get('groov_apple_error');
    if (!code && !error) return null;

    // Scrub first, unconditionally: the code is single-use, so leaving it in the
    // URL only creates a refresh that fails.
    params.delete('groov_apple');
    params.delete('groov_apple_error');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);

    if (error) return { error: error };

    const res = await fetch(`${GROOV_API}/session/apple/handoff`, {
      method: 'POST',
      headers: groovHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ code: code })
    });
    const data = await parseResponse(res);
    if (data.token) {
      localStorage.setItem(GROOV_TOKEN_KEY, data.token);
    }
    return data;
  },

  /** Why the last Apple sign-in failed, if it did — cleared as it is read, so it
   *  shows once.
   *
   *  A failure has to survive one navigation to be seen at all: the customer lands
   *  back on the account page, which sends a signed-out visitor to /pages/login,
   *  and the URL parameter is scrubbed before either page runs. The login page is
   *  where the Apple button lives, so that is where the message belongs. */
  takeAppleError() {
    try {
      const reason = sessionStorage.getItem(GROOV_APPLE_ERROR_KEY);
      if (reason) sessionStorage.removeItem(GROOV_APPLE_ERROR_KEY);
      return reason;
    } catch (e) {
      return null;
    }
  },

  /** Log out (destroys session on backend and clears local token). */
  async logout() {
    try {
      await fetch(`${GROOV_API}/session`, {
        method: 'DELETE',
        headers: groovHeaders()
      });
    } catch (e) {
      console.error('Logout request failed:', e);
    }
    localStorage.removeItem(GROOV_TOKEN_KEY);
  },

  /** Register a new account. Returns { message } on success or { error } on failure. */
  async register({ email, password, passwordConfirmation, firstName, lastName, emailMarketing }) {
    const res = await fetch(`${GROOV_API}/registrations`, {
      method: 'POST',
      headers: groovHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        user: {
          email_address: email,
          password: password,
          password_confirmation: passwordConfirmation,
          first_name: firstName,
          last_name: lastName,
          email_marketing: emailMarketing,
          signup_method: 'shopify_web'
        }
      })
    });
    const data = await parseResponse(res);
    if (data.token) {
      localStorage.setItem(GROOV_TOKEN_KEY, data.token);
    }
    return data;
  },

  /** Request a password reset email. */
  async forgotPassword(email) {
    const res = await fetch(`${GROOV_API}/passwords`, {
      method: 'POST',
      headers: groovHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ email_address: email })
    });
    return await parseResponse(res);
  },

  /**
   * The second half of "Forgot your password?": the link in the reset email lands
   * on /pages/reset-password?token=…, and this sets the new password on the Groov
   * account. The same endpoint also accepts the longer-lived account-setup token a
   * person gets when someone created the account for them (a doctor adding a
   * patient to a roster), so "set a password for the first time" and "reset" are
   * one page. Returns { message } or { error }.
   */
  async resetPassword(token, password, passwordConfirmation) {
    const res = await fetch(`${GROOV_API}/passwords/${encodeURIComponent(token)}`, {
      method: 'PATCH',
      headers: groovHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ password: password, password_confirmation: passwordConfirmation })
    });
    return await parseResponse(res);
  },

  // ── Checkout ──────────────────────────────────────────────

  /**
   * Send the current Shopify cart to the backend to create a checkout.
   * The backend creates a Storefront API cart with _PlayerID attached,
   * and returns { checkout_url }.
   */
  async createCheckout({ groovOrderToken } = {}) {
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();

    if (!cart.items || cart.items.length === 0) {
      return { error: 'Cart is empty' };
    }

    // The backend REPLACES this cart with one it builds, so each line's
    // properties go with it or they are lost. Rise.ai keeps the gift
    // recipient (Name, Email, Message) there; without this a signed-in
    // gift buyer's recipient never reached Shopify.
    const items = cart.items.map(item => ({
      variant_id: String(item.variant_id),
      quantity: item.quantity,
      properties: item.properties || {}
    }));

    const body = { items };
    if (groovOrderToken) {
      body.groov_order_token = groovOrderToken;
    }

    const res = await fetch(`${GROOV_API}/consumer_orders/web_checkout`, {
      method: 'POST',
      headers: groovHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    });

    return await parseResponse(res);
  },

  // ── App Clip ──────────────────────────────────────────────

  /**
   * Look up an order by groov_order_token. Returns { scan_link, order_slug } or { error }.
   * Polls with retries because the Shopify webhook that creates the order may be delayed.
   */
  async getOrderByToken(token, { maxAttempts = 40, intervalMs = 3000 } = {}) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await fetch(
          `${GROOV_API}/consumer_orders/by_token?token=${encodeURIComponent(token)}`,
          {
            method: 'GET',
            headers: { 'Accept': 'application/json' }
          }
        );
        if (res.ok) {
          return await res.json();
        }
        if (res.status !== 404) {
          return await parseResponse(res);
        }
      } catch (e) {
        console.warn(`[GroovBackend] by_token attempt ${attempt} failed:`, e);
      }
      if (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, intervalMs));
      }
    }
    return { error: 'timeout', message: 'Order is still processing. Please check your account page.' };
  },

  // ── User ──────────────────────────────────────────────────

  /** Get the authenticated user's B2C orders. */
  async getOrders() {
    const res = await fetch(`${GROOV_API}/orders`, {
      method: 'GET',
      headers: groovHeaders()
    });
    return await parseResponse(res);
  },

  /**
   * Get one order's detail: line items, shipping address, payment, tracking.
   *
   * The summary from getOrders() is enough to see THAT an order exists; this is
   * what shows whether its contents are right. That matters most during the
   * legacy migration — the v3 iOS app deliberately drops order history, so this
   * page is where QA verifies a migrated order's line items actually landed.
   * Endpoint has existed since the consumer API was written; nothing called it.
   */
  async getOrder(slug) {
    const res = await fetch(`${GROOV_API}/orders/${encodeURIComponent(slug)}`, {
      method: 'GET',
      headers: groovHeaders()
    });
    return await parseResponse(res);
  },

  /** Get current user profile. */
  async getMe() {
    const res = await fetch(`${GROOV_API}/me`, {
      method: 'GET',
      headers: groovHeaders()
    });
    return await parseResponse(res);
  }
};


/**
 * Accountless checkout — no Groov login required.
 * Generates a unique tracking ID, stores it in localStorage AND as a Shopify cart
 * attribute (which flows through to order note_attributes). Then redirects to /checkout.
 */
async function groovAccountlessCheckout(btn) {
  btn.disabled = true;
  btn.textContent = 'Processing...';

  var trackingId = crypto.randomUUID();
  localStorage.setItem('groov_order_token', trackingId);

  try {
    // Signed-in: backend creates a Storefront API cart with email + tracking token
    if (GroovBackend.hasToken()) {
      var result = await GroovBackend.createCheckout({ groovOrderToken: trackingId });
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      console.warn('[GroovBackend] Backend checkout failed, falling back:', result.error);
    }

    // Not signed in (or backend failed): set token on Shopify's local cart, then redirect
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // _groov_flow: the backend's orders/create webhook ignores an order that carries neither it nor _PlayerID.
      body: JSON.stringify({ attributes: { '_groov_order_token': trackingId, '_groov_flow': 'consumer' } })
    });
  } catch (e) {
    console.warn('[GroovBackend] Checkout error:', e);
  }

  window.location.href = '/checkout';
}

window.GroovBackend = GroovBackend;
window.groovAccountlessCheckout = groovAccountlessCheckout;

// Redeem an Apple sign-in return before anything else reads the session.
//
// This has to run on EVERY page, not just the ones that render the Apple button:
// the customer comes back to whatever page they left from, and the account page —
// the usual landing spot — has no button on it. It exits immediately when the URL
// carries no Apple parameters, which is every other page load.
//
// Fires a `groov:apple-signin` event so a page can react (re-render as signed in,
// or show the error) without this file needing to know what pages exist.
groovAppleReturn = GroovBackend.consumeAppleReturn()
  .then(function (result) {
    if (!result) return null;
    if (result.error) {
      try { sessionStorage.setItem(GROOV_APPLE_ERROR_KEY, result.error); } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('groov:apple-signin', { detail: result }));
    return result;
  })
  .catch(function (e) {
    console.warn('[GroovBackend] Apple sign-in return failed:', e);
    return null;
  });
